import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import {
    MaxNotificationLinkCodeEntity,
    MaxNotificationSubscriptionEntity,
} from '@/entities/max-notification-subscription.entity';
import { useAuth } from '@/context/auth.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useCallback, useEffect, useState } from 'react';
import { toast } from '@/ui/toast';

type UseSettingsMaxNotificationsModalControllerArgs = {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
};

const settingsMaxNotificationsLogger = createClientLogger({
    scope: 'settings-max-notifications',
});

const MAX_INTERVAL_MINUTES = 1;
const MAX_INTERVAL_MAX_MINUTES = 10080;

const toRecipientTitle = (subscription: MaxNotificationSubscriptionEntity): string => {
    const displayName = (subscription.recipientDisplayName || '').trim();
    if (displayName) {
        return displayName;
    }

    return `${subscription.recipientKind}:${subscription.recipientId}`;
};

const normalizeIntervalsById = (subscriptions: MaxNotificationSubscriptionEntity[]): Record<string, string> => {
    const nextIntervalsById: Record<string, string> = {};

    for (const subscription of subscriptions) {
        nextIntervalsById[subscription.id] = String(subscription.intervalMinutes);
    }

    return nextIntervalsById;
};

const isValidIntervalDraft = (value: string): boolean => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= MAX_INTERVAL_MINUTES && parsed <= MAX_INTERVAL_MAX_MINUTES;
};

export type SettingsMaxNotificationsModalControllerBindings = {
    subscriptions: MaxNotificationSubscriptionEntity[];
    latestLinkCode: MaxNotificationLinkCodeEntity | null;
    intervalsBySubscriptionId: Record<string, string>;
    isLoadingSubscriptions: boolean;
    isGeneratingLinkCode: boolean;
    savingSubscriptionIds: string[];
    onIntervalChanged: (subscriptionId: string, value: string) => void;
    saveInterval: (subscriptionId: string) => Promise<void>;
    issueLinkCode: () => Promise<void>;
    copyLinkCode: () => Promise<void>;
    closeModal: () => void;
    recipientTitleOf: (subscription: MaxNotificationSubscriptionEntity) => string;
    isIntervalDraftValid: (subscriptionId: string) => boolean;
};

export const useSettingsMaxNotificationsModalController = ({
    isShown,
    setIsShown,
}: UseSettingsMaxNotificationsModalControllerArgs): SettingsMaxNotificationsModalControllerBindings => {
    const { accessToken } = useAuth();
    const { maxNotificationsApi } = useMarketplaceApi();

    const [subscriptions, setSubscriptions] = useState<MaxNotificationSubscriptionEntity[]>([]);
    const [latestLinkCode, setLatestLinkCode] = useState<MaxNotificationLinkCodeEntity | null>(null);
    const [intervalsBySubscriptionId, setIntervalsBySubscriptionId] = useState<Record<string, string>>({});
    const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
    const [isGeneratingLinkCode, setIsGeneratingLinkCode] = useState(false);
    const [savingSubscriptionIds, setSavingSubscriptionIds] = useState<string[]>([]);

    const loadSubscriptions = useCallback(
        async (signal: AbortSignal) => {
            setIsLoadingSubscriptions(true);

            try {
                const response = await maxNotificationsApi.getSubscriptions({
                    authToken: accessToken,
                    signal,
                });
                setSubscriptions(response.data);
                setIntervalsBySubscriptionId(normalizeIntervalsById(response.data));
            } catch (error) {
                if (isRequestCanceled(error)) {
                    return;
                }

                logClientError({
                    logger: settingsMaxNotificationsLogger,
                    message: '[client.settings-max.subscriptions-load] Failed to load MAX subscriptions.',
                    error,
                });
                toast.error('Не удалось загрузить MAX-подписки');
            } finally {
                setIsLoadingSubscriptions(false);
            }
        },
        [accessToken, maxNotificationsApi],
    );

    useEffect(() => {
        if (!isShown) {
            return;
        }

        const abortController = new AbortController();
        void loadSubscriptions(abortController.signal);

        return () => {
            abortController.abort();
        };
    }, [isShown, loadSubscriptions]);

    const onIntervalChanged = useCallback((subscriptionId: string, value: string) => {
        setIntervalsBySubscriptionId((prevState) => ({
            ...prevState,
            [subscriptionId]: value,
        }));
    }, []);

    const saveInterval = useCallback(
        async (subscriptionId: string) => {
            const intervalDraft = intervalsBySubscriptionId[subscriptionId];
            if (!isValidIntervalDraft(intervalDraft || '')) {
                toast.error(
                    `Интервал должен быть целым числом от ${MAX_INTERVAL_MINUTES} до ${MAX_INTERVAL_MAX_MINUTES}`,
                );
                return;
            }

            const intervalMinutes = Number(intervalDraft);
            setSavingSubscriptionIds((prevState) => [...new Set([...prevState, subscriptionId])]);

            try {
                const response = await maxNotificationsApi.updateSubscriptionInterval(
                    {
                        subscriptionId,
                        intervalMinutes,
                    },
                    {
                        authToken: accessToken,
                    },
                );

                setSubscriptions((prevState) =>
                    prevState.map((subscription) =>
                        subscription.id === subscriptionId ? response.data : subscription,
                    ),
                );
                setIntervalsBySubscriptionId((prevState) => ({
                    ...prevState,
                    [subscriptionId]: String(response.data.intervalMinutes),
                }));

                toast.success('Интервал уведомлений обновлен');
            } catch (error) {
                logClientError({
                    logger: settingsMaxNotificationsLogger,
                    message:
                        '[client.settings-max.subscription-interval-update] Failed to update MAX subscription interval.',
                    error,
                    context: {
                        subscriptionId,
                    },
                });
                toast.error('Не удалось обновить интервал');
            } finally {
                setSavingSubscriptionIds((prevState) => prevState.filter((id) => id !== subscriptionId));
            }
        },
        [accessToken, intervalsBySubscriptionId, maxNotificationsApi],
    );

    const issueLinkCode = useCallback(async () => {
        setIsGeneratingLinkCode(true);

        try {
            const response = await maxNotificationsApi.issueLinkCode({}, { authToken: accessToken });
            setLatestLinkCode(response.data);
            toast.success('Ссылка для подключения к MAX создана');
        } catch (error) {
            logClientError({
                logger: settingsMaxNotificationsLogger,
                message: '[client.settings-max.link-code-issue] Failed to issue MAX link code.',
                error,
            });
            toast.error('Не удалось создать ссылку');
        } finally {
            setIsGeneratingLinkCode(false);
        }
    }, [accessToken, maxNotificationsApi]);

    const copyLinkCode = useCallback(async () => {
        const deepLink = latestLinkCode?.deepLink;
        if (!deepLink) {
            return;
        }

        try {
            await navigator.clipboard.writeText(deepLink);
            toast.success('Ссылка скопирована');
        } catch (error) {
            logClientError({
                logger: settingsMaxNotificationsLogger,
                message: '[client.settings-max.link-code-copy] Failed to copy MAX link code.',
                error,
            });
            toast.error('Не удалось скопировать ссылку');
        }
    }, [latestLinkCode?.deepLink]);

    const closeModal = useCallback(() => {
        setIsShown(false);
    }, [setIsShown]);

    const isIntervalDraftValid = useCallback(
        (subscriptionId: string): boolean => isValidIntervalDraft(intervalsBySubscriptionId[subscriptionId] || ''),
        [intervalsBySubscriptionId],
    );

    return {
        subscriptions,
        latestLinkCode,
        intervalsBySubscriptionId,
        isLoadingSubscriptions,
        isGeneratingLinkCode,
        savingSubscriptionIds,
        onIntervalChanged,
        saveInterval,
        issueLinkCode,
        copyLinkCode,
        closeModal,
        recipientTitleOf: toRecipientTitle,
        isIntervalDraftValid,
    };
};
